import { useCallback } from 'react';

import { selectizeService } from '@personal-trainer-app/shared/src/services';

import type { SelectizeOption } from '@/types/forms';
import type { SelectizeResource } from '@personal-trainer-app/shared/src/types/domain/selectize.types';

/** Options accepted by {@link useSelectizeLoader}. */
export interface UseSelectizeLoaderOptions {
  /**
   * When `true` (default) only `{ value, label }` is loaded. Pass `false` to
   * request the resource's full projection (`workout` | `plan` | `user_goal`);
   * the extra fields are preserved verbatim on each option's `data` property
   * so a `renderOption` can surface them (e.g. as a subtitle).
   */
  compact?: boolean;
}

/**
 * Produce a stable `loadOptions` callback for `useSelectize`/`FormSelectize`,
 * backed by the backend quick-search endpoint
 * (`GET /api/selectize/{resource}`).
 *
 * By default the endpoint returns the lean `{ value, label }` shape, so the
 * returned function plugs straight into a remote-loaded selectize. Pair it with
 * `creatable` and `minSearchLength={0}` to let users pick an existing value or
 * type a brand-new one, and to surface suggestions as soon as the field is
 * focused.
 *
 * Pass `{ compact: false }` to load the resource's full projection; the extra
 * fields are tucked onto each option's `data` property (the `value`/`label`
 * stay flat for selection and display).
 *
 * @param resource - Which suggestion set to load (e.g. `body_parts`, `workout`)
 * @param options - {@link UseSelectizeLoaderOptions}; pass `{ compact: false }` for full data
 * @returns A memoized `(query: string) => Promise<SelectizeOption[]>` loader
 *
 * @example
 * const loadBodyParts = useSelectizeLoader('body_parts');
 * <FormSelectize loadOptions={loadBodyParts} creatable minSearchLength={0} />
 *
 * @example
 * const loadWorkouts = useSelectizeLoader('workout', { compact: false });
 * <FormSelectize
 *   loadOptions={loadWorkouts}
 *   renderOption={(opt) => <WorkoutRow workout={opt.data} />}
 * />
 */
export function useSelectizeLoader(
  resource: SelectizeResource,
  options: UseSelectizeLoaderOptions = {}
): (query: string) => Promise<SelectizeOption[]> {
  const compact = options.compact ?? true;

  return useCallback(
    async (query: string): Promise<SelectizeOption[]> => {
      const suggestions = await selectizeService.search(resource, query, {
        compact,
      });
      return suggestions.map((suggestion) => ({
        value: suggestion.value,
        label: suggestion.label,
        // Full-mode extras ride along on `data`; consumers cast it back to the
        // resource's suggestion type in their `renderOption`.
        ...(compact
          ? {}
          : { data: suggestion as unknown as Record<string, unknown> }),
      }));
    },
    [resource, compact]
  );
}

export default useSelectizeLoader;
